import { type ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { FormModal } from '../Formik';
import Chip from '../InputFields/Chip';

import type { FormikFormField, FormModalProps } from '@/types/formTypes';

interface DeleteFormModalProps {
  fieldName: string;
  fieldValue: string;
  messages?: string[];
  onSubmit: FormModalProps['onSubmit'];
  onCancel: () => void;
  children?: ReactNode;
}

const DeleteFormModal = ({
  fieldName,
  fieldValue,
  messages = [],
  onSubmit,
  onCancel,
  children = null,
}: DeleteFormModalProps) => {
  const intl = useIntl();

  const fields = useMemo(
    () => [
      {
        fieldType: 'typography',
        name: 'deleteMessage',
        fieldContent: (
          <>
            {intl.formatMessage(
              { id: 'deleteMessage1' },
              { name: fieldName.toLowerCase(), value: fieldValue }
            )}
          </>
        ),
      },
      ...(children
        ? [
            {
              fieldType: 'grid',
              fieldContent: children,
            },
          ]
        : []),
      ...messages.map((messageId, index) => ({
        fieldType: 'grid',
        name: `message-${String(index)}`,
        fieldContent: <>{intl.formatMessage({ id: messageId })}</>,
      })),
      {
        fieldType: 'grid',
        name: 'deleteMessage3:',
        item: true,
        justifyContent: 'center',
        fieldContent: (
          <strong>
            {intl.formatMessage({ id: 'deleteMessage2' })}
            <Chip
              label={fieldValue}
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
          </strong>
        ),
      },
      {
        fieldType: 'text',
        name: 'deleteInput',
      },
    ],
    [intl, fieldName, fieldValue, children, messages]
  );

  const initialValues = {
    deleteInput: '',
  };

  const validationSchema = Yup.object().shape({
    deleteInput: Yup.string()
      .oneOf(
        [fieldValue, undefined],
        intl.formatMessage({ id: 'deleteFieldMismatch' }, { name: fieldName })
      )
      .required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  return (
    <FormModal
      title={intl.formatMessage({ id: 'deleteModalTitle' }, { name: fieldName })}
      fields={fields as FormikFormField[]}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitLabel={intl.formatMessage({ id: 'delete' })}
    />
  );
};

export default DeleteFormModal;
